import { Helmet, HelmetProvider } from "react-helmet-async";
import { useParams, useLocation } from "react-router-dom";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "../../config/languages";

const SeoHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  structuredData,
  json
}) => {
  const { lang } = useParams();
  const location = useLocation();
  const currentLang = lang || DEFAULT_LANGUAGE;

  const defaultTitle =
    "Citizen Hub Immigration Services - Professional Immigration Form Assistance | Expert Legal Guidance";
  const defaultDescription =
    "Professional immigration services in the United States. Expert assistance with U.S. immigration forms, lawyer matching, and direct payment processing. Specializing in family petitions, employment visas, green cards, and citizenship applications. Get started today!";
  const defaultKeywords =
    "immigration services, immigration forms, U.S. immigration, immigration lawyers, green card applications, citizenship forms, family petitions, employment visas, asylum applications, immigration assistance, legal services, immigration platform";
  const defaultOgImage = "https://citznhub.com/og-image.jpg";
  const baseUrl = "https://citznhub.com";

  // Get the current path without the language prefix
  const pathWithoutLang = location.pathname.split('/').slice(2).join('/');

  // Generate canonical URL (current language)
  const finalCanonicalUrl = canonicalUrl || `${baseUrl}/${currentLang}${pathWithoutLang ? '/' + pathWithoutLang : ''}`;

  // Generate hreflang URLs for all supported languages
  const hreflangs = SUPPORTED_LANGUAGES.map(language => ({
    lang: language.code,
    url: `${baseUrl}/${language.code}${pathWithoutLang ? '/' + pathWithoutLang : ''}`
  }));

  // x-default URL (default language)
  const xDefaultUrl = `${baseUrl}/${DEFAULT_LANGUAGE}${pathWithoutLang ? '/' + pathWithoutLang : ''}`;

  // Get locale code for Open Graph (e.g., en_US, es_ES, fr_FR)
  const getOgLocale = (langCode) => {
    const localeMap = {
      'en': 'en_US',
      'es': 'es_ES',
      'fr': 'fr_FR'
    };
    return localeMap[langCode] || 'en_US';
  };

  return (
    <HelmetProvider>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{title || defaultTitle}</title>
        <meta name="title" content={title || defaultTitle} />
        <meta name="description" content={description || defaultDescription} />
        <meta name="keywords" content={keywords || defaultKeywords} />
        <meta name="author" content="Citizen Hub Immigration Services" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content={currentLang} />
        <meta name="revisit-after" content="7 days" />

        {/* Canonical URL */}
        <link rel="canonical" href={finalCanonicalUrl} />

        {/* hreflang tags for all supported languages */}
        {hreflangs.map(({ lang: hreflangCode, url }) => (
          <link
            key={hreflangCode}
            rel="alternate"
            hrefLang={hreflangCode}
            href={url}
          />
        ))}

        {/* x-default hreflang */}
        <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={finalCanonicalUrl} />
        <meta property="og:title" content={title || defaultTitle} />
        <meta
          property="og:description"
          content={description || defaultDescription}
        />
        <meta property="og:image" content={ogImage || defaultOgImage} />
        <meta
          property="og:site_name"
          content="Citizen Hub Immigration Services"
        />
        <meta property="og:locale" content={getOgLocale(currentLang)} />

        {/* Alternate locales for Open Graph */}
        {SUPPORTED_LANGUAGES.filter(l => l.code !== currentLang).map(language => (
          <meta
            key={language.code}
            property="og:locale:alternate"
            content={getOgLocale(language.code)}
          />
        ))}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={finalCanonicalUrl} />
        <meta property="twitter:title" content={title || defaultTitle} />
        <meta
          property="twitter:description"
          content={description || defaultDescription}
        />
        <meta property="twitter:image" content={ogImage || defaultOgImage} />

        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />
        <meta name="geo.position" content="39.8283;-98.5795" />
        <meta name="ICBM" content="39.8283, -98.5795" />
        <meta name="theme-color" content="#2563eb" />

        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>
    </HelmetProvider>
  );
};

export default SeoHead;
